import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { COUNTRIES, US_STATES, CITIES_BY_STATE } from '../config/usLocationsConfig';
import CustomSelect from '../components/ui/CustomSelect';
import Card from '../components/ui/Card';
import ReliabilityBar from '../components/ui/ReliabilityBar';
import MatchHistory from '../components/MatchHistory';
import EloChart from '../components/EloChart';
import './PlayerProfile.css';

/* ─────────────────────────────────────────────────────────────
   PLAYER PROFILE — public view of any player's stats + identity.

   Accessed via /profile/:username. Looks up the profile by
   username, then uses the UUID (profile.id) for all friendship
   and club queries so those remain unaffected.

   When the logged-in user views their own profile they see an
   "Edit Profile" button that lets them update their bio, gear,
   grip, playstyle, and avatar in-place.
   ───────────────────────────────────────────────────────────── */

const GRIP_OPTIONS      = ['Shakehand', 'Penhold', 'Seemiller', 'Other'];
const PLAYSTYLE_OPTIONS = ['Offensive', 'Defensive', 'All-Round', 'Other'];

function PlayerProfile() {
  const { username } = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const avatarInputRef = useRef(null);

  const [profile, setProfile]   = useState(null);
  const [clubs, setClubs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Friendship state: null | 'none' | 'pending_sent' | 'pending_received' | 'accepted'
  const [friendStatus,  setFriendStatus]  = useState(null);
  const [friendshipId,  setFriendshipId]  = useState(null);
  const [friendLoading, setFriendLoading] = useState(false);

  // ── Singles / Doubles mode ───────────────────────────────────
  const [statsMode, setStatsMode] = useState('singles');

  // ── Edit mode (own profile only) ─────────────────────────────
  const [editMode,      setEditMode]      = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Draft values — populated when entering edit mode
  const [draftBio,      setDraftBio]      = useState('');
  const [draftGrip,     setDraftGrip]     = useState('');
  const [draftPlaystyle, setDraftPlaystyle] = useState('');
  const [draftPaddle,   setDraftPaddle]   = useState(''); // simple mode
  const [draftBlade,    setDraftBlade]    = useState('');
  const [draftFhRubber, setDraftFhRubber] = useState('');
  const [draftBhRubber, setDraftBhRubber] = useState('');
  // advancedGear: true when the user wants blade/fh/bh separate fields
  const [advancedGear,  setAdvancedGear]  = useState(false);

  // Location draft
  const [draftCountry,     setDraftCountry]     = useState('');
  const [draftStateRegion, setDraftStateRegion] = useState('');
  const [draftCity,        setDraftCity]        = useState('');

  // Step 1: fetch profile by username
  useEffect(() => {
    fetchProfile();
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: once profile.id is known, fetch clubs + friend status
  useEffect(() => {
    if (!profile) return;
    fetchClubs(profile.id);
    if (user && user.id !== profile.id) fetchFriendStatus(profile.id);
  }, [profile?.id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchProfile() {
    setLoading(true);
    setNotFound(false);
    setProfile(null);
    setEditMode(false);

    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, first_name, full_name, elo_rating, is_rated, wins, losses, matches_played, unique_opponents_count, doubles_elo, doubles_wins, doubles_losses, doubles_matches_played, doubles_unique_opponents_count, avatar_url, bio, grip, playstyle, blade, fh_rubber, bh_rubber, country, state_region, city')
      .eq('username', username)
      .single();

    if (error || !data) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setProfile(data);
    setLoading(false);
  }

  async function fetchClubs(profileId) {
    const { data } = await supabase
      .from('club_memberships')
      .select('clubs(id, name, is_verified)')
      .eq('user_id', profileId);
    setClubs((data || []).map(m => m.clubs).filter(Boolean));
  }

  async function fetchFriendStatus(profileId) {
    const { data } = await supabase
      .from('friendships')
      .select('id, status, user_id, friend_id')
      .or(`and(user_id.eq.${user.id},friend_id.eq.${profileId}),and(user_id.eq.${profileId},friend_id.eq.${user.id})`)
      .maybeSingle();

    if (!data) {
      setFriendStatus('none');
    } else if (data.status === 'accepted') {
      setFriendStatus('accepted');
      setFriendshipId(data.id);
    } else if (data.status === 'pending' && data.user_id === user.id) {
      setFriendStatus('pending_sent');
      setFriendshipId(data.id);
    } else if (data.status === 'pending' && data.friend_id === user.id) {
      setFriendStatus('pending_received');
      setFriendshipId(data.id);
    }
  }

  async function handleRespond(accept) {
    setFriendLoading(true);
    await supabase.rpc('respond_to_friend_request', {
      p_friendship_id: friendshipId,
      p_accept:        accept,
    });
    setFriendStatus(accept ? 'accepted' : 'none');
    setFriendLoading(false);
  }

  async function handleAddFriend() {
    if (!user) { navigate('/login'); return; }
    setFriendLoading(true);
    await supabase.rpc('send_friend_request', { p_friend_id: profile.id });
    setFriendStatus('pending_sent');
    setFriendLoading(false);
  }

  // ── Enter / exit edit mode ───────────────────────────────────
  function enterEditMode() {
    setDraftBio(profile.bio || '');
    setDraftGrip(profile.grip || '');
    setDraftPlaystyle(profile.playstyle || '');
    setDraftBlade(profile.blade || '');
    setDraftFhRubber(profile.fh_rubber || '');
    setDraftBhRubber(profile.bh_rubber || '');
    // If the user already has fh or bh rubber set, open in advanced mode
    const hasAdvanced = !!(profile.fh_rubber || profile.bh_rubber);
    setAdvancedGear(hasAdvanced);
    // Simple paddle field mirrors blade when not in advanced mode
    setDraftPaddle(profile.blade || '');
    // Location
    setDraftCountry(profile.country || '');
    setDraftStateRegion(profile.state_region || '');
    setDraftCity(profile.city || '');
    setSaveError('');
    setEditMode(true);
  }

  // Toggle between simple and advanced gear — carry values across
  function toggleAdvancedGear(on) {
    if (on) {
      // Switching to advanced: pre-fill blade with the simple paddle value
      setDraftBlade(draftPaddle);
      setDraftFhRubber('');
      setDraftBhRubber('');
    } else {
      // Switching to simple: carry blade back, drop fh/bh
      setDraftPaddle(draftBlade);
    }
    setAdvancedGear(on);
  }

  function cancelEdit() {
    setEditMode(false);
    setSaveError('');
  }

  // ── Save profile identity fields ─────────────────────────────
  async function saveProfile() {
    setSaving(true);
    setSaveError('');

    // In simple mode, save the paddle value to blade and clear fh/bh
    const saveBlde  = advancedGear ? draftBlade.trim()    : draftPaddle.trim();
    const saveFh    = advancedGear ? draftFhRubber.trim() : null;
    const saveBh    = advancedGear ? draftBhRubber.trim() : null;

    const { error } = await supabase
      .from('profiles')
      .update({
        bio:          draftBio.trim()          || null,
        grip:         draftGrip                || null,
        playstyle:    draftPlaystyle           || null,
        blade:        saveBlde                 || null,
        fh_rubber:    saveFh                   || null,
        bh_rubber:    saveBh                   || null,
        country:      draftCountry             || null,
        state_region: draftStateRegion.trim()  || null,
        city:         draftCity.trim()         || null,
      })
      .eq('id', user.id);

    if (error) {
      setSaveError('Could not save. Please try again.');
      setSaving(false);
      return;
    }

    // Update local profile state so the view reflects changes immediately
    setProfile(prev => ({
      ...prev,
      bio:          draftBio.trim()          || null,
      grip:         draftGrip                || null,
      playstyle:    draftPlaystyle           || null,
      blade:        saveBlde                 || null,
      fh_rubber:    saveFh                   || null,
      bh_rubber:    saveBh                   || null,
      country:      draftCountry             || null,
      state_region: draftStateRegion.trim()  || null,
      city:         draftCity.trim()         || null,
    }));

    setSaving(false);
    setEditMode(false);
  }

  // ── Avatar upload ────────────────────────────────────────────
  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Only allow safe raster image types (SVG is an XSS vector)
    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!ALLOWED_TYPES.includes(file.type)) return;
    if (file.size > 2 * 1024 * 1024) {
      setSaveError('Image must be under 2 MB.');
      return;
    }

    setUploadingAvatar(true);
    setSaveError('');

    // Always store at a fixed path so it overwrites the old one
    const path = `${user.id}/avatar`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      setSaveError('Could not upload image. Please try again.');
      setUploadingAvatar(false);
      return;
    }

    // Get the public URL and save to the profile row
    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(path);

    // Add a cache-bust so the browser doesn't show the old image
    const publicUrl = urlData.publicUrl + '?t=' + Date.now();

    const { error: dbError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', user.id);

    if (dbError) {
      setSaveError('Image uploaded but could not save URL. Please try again.');
      setUploadingAvatar(false);
      return;
    }

    setProfile(prev => ({ ...prev, avatar_url: publicUrl }));
    setUploadingAvatar(false);
  }

  // ── Derived stats ────────────────────────────────────────────
  const totalSingles  = profile ? profile.wins + profile.losses : 0;
  const singlesWinRate = totalSingles > 0
    ? Math.round((profile.wins / totalSingles) * 100) + '%'
    : '—';
  const totalDoubles   = profile ? (profile.doubles_wins ?? 0) + (profile.doubles_losses ?? 0) : 0;
  const doublesWinRate = totalDoubles > 0
    ? Math.round(((profile.doubles_wins ?? 0) / totalDoubles) * 100) + '%'
    : '—';
  const winRate      = statsMode === 'doubles' ? doublesWinRate : singlesWinRate;
  const isOwnProfile = user?.id === profile?.id;

  // Does this profile have any gear set?
  const hasGear = profile && (profile.blade || profile.fh_rubber || profile.bh_rubber);

  // ── Loading ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-inner">
          <div className="profile-loading">
            <span className="profile-loading-star">★</span>
            <span className="profile-loading-star">★</span>
            <span className="profile-loading-star">★</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────
  if (notFound) {
    return (
      <div className="profile-page">
        <div className="profile-inner">
          <div className="profile-not-found">
            <p className="profile-not-found-title">Player not found</p>
            <p className="profile-not-found-sub">This profile doesn't exist or has been removed.</p>
            <button className="profile-back-btn" onClick={() => navigate(-1)}>
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Friend button ────────────────────────────────────────────
  function FriendButton() {
    if (isOwnProfile) return null;
    if (friendStatus === null) return null;

    if (friendStatus === 'accepted') {
      return <span className="profile-friend-status">Friends</span>;
    }
    if (friendStatus === 'pending_sent') {
      return <span className="profile-friend-status profile-friend-pending">Request Sent</span>;
    }
    if (friendStatus === 'pending_received') {
      return (
        <div className="profile-friend-respond">
          <span className="profile-friend-respond-label">Friend Request</span>
          <button className="profile-friend-accept" disabled={friendLoading}
            onClick={() => handleRespond(true)}>
            Accept
          </button>
          <button className="profile-friend-decline" disabled={friendLoading}
            onClick={() => handleRespond(false)}>
            Decline
          </button>
        </div>
      );
    }
    return (
      <button
        className="profile-add-friend-btn"
        onClick={handleAddFriend}
        disabled={friendLoading}
      >
        {friendLoading ? 'Sending...' : '+ Add Friend'}
      </button>
    );
  }

  // Use first_name for the avatar initial, fall back to username
  const avatarInitial = (profile.first_name || profile.username || '?').charAt(0).toUpperCase();

  return (
    <div className="profile-page">
      <div className="profile-inner">

        <button className="profile-back-btn" onClick={() => editMode ? cancelEdit() : navigate(-1)}>
          ← Back
        </button>

        {/* ── Player header ──────────────────────────────────── */}
        <div className="profile-header">

          {/* Avatar — clickable to upload when in edit mode */}
          <div
            className={`profile-avatar-wrap ${editMode ? 'profile-avatar-editable' : ''}`}
            onClick={editMode ? () => avatarInputRef.current?.click() : undefined}
            title={editMode ? 'Click to change photo' : undefined}
          >
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.username}
                className="profile-avatar profile-avatar-img"
              />
            ) : (
              <div className="profile-avatar">{avatarInitial}</div>
            )}
            {editMode && (
              <div className="profile-avatar-overlay">
                {uploadingAvatar ? '…' : '📷'}
              </div>
            )}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="profile-avatar-input"
              onChange={handleAvatarUpload}
            />
          </div>

          <div className="profile-header-info">
            <h1 className="profile-name">{profile.username}</h1>
            {profile.full_name && (
              <p className="profile-full-name">{profile.full_name}</p>
            )}
            {/* Location line — shown read-only when at least one field is set */}
            {!editMode && (profile.city || profile.state_region || profile.country) && (
              <p className="profile-location">
                {[
                  profile.city,
                  profile.state_region,
                  profile.country,
                ].filter(Boolean).join(', ')}
              </p>
            )}
            {isOwnProfile && !editMode && (
              <span className="profile-you-tag">Your profile</span>
            )}
          </div>

          <div className="profile-header-actions">
            {isOwnProfile && !editMode && (
              <button className="profile-edit-btn" onClick={enterEditMode}>
                Edit Profile
              </button>
            )}
            <FriendButton />
          </div>
        </div>

        {/* ── Bio ────────────────────────────────────────────── */}
        {editMode ? (
          <div className="profile-edit-section">
            <label className="profile-edit-label">Bio</label>
            <textarea
              className="profile-edit-textarea"
              placeholder="Tell other players a bit about yourself…"
              maxLength={160}
              value={draftBio}
              onChange={e => setDraftBio(e.target.value)}
              rows={3}
            />
            <span className="profile-edit-charcount">{draftBio.length} / 160</span>
          </div>
        ) : (
          profile.bio && (
            <p className="profile-bio">{profile.bio}</p>
          )
        )}

        {/* ── Player Info (grip + playstyle) ─────────────────── */}
        {editMode ? (
          <div className="profile-edit-section">
            <label className="profile-edit-label">Player Info</label>
            <div className="profile-edit-row">
              <div className="profile-edit-field">
                <span className="profile-edit-sublabel">Grip Style</span>
                <CustomSelect
                  value={draftGrip}
                  onChange={v => setDraftGrip(v)}
                  placeholder="Not set"
                  options={[
                    { value: '', label: 'Not set' },
                    ...GRIP_OPTIONS.map(g => ({ value: g, label: g })),
                  ]}
                />
              </div>
              <div className="profile-edit-field">
                <span className="profile-edit-sublabel">Play Style</span>
                <CustomSelect
                  value={draftPlaystyle}
                  onChange={v => setDraftPlaystyle(v)}
                  placeholder="Not set"
                  options={[
                    { value: '', label: 'Not set' },
                    ...PLAYSTYLE_OPTIONS.map(p => ({ value: p, label: p })),
                  ]}
                />
              </div>
            </div>
          </div>
        ) : (
          (profile.grip || profile.playstyle) && (
            <div className="profile-info-chips">
              {profile.grip && (
                <span className="profile-info-chip">
                  <span className="profile-info-chip-label">Grip</span>
                  {profile.grip}
                </span>
              )}
              {profile.playstyle && (
                <span className="profile-info-chip">
                  <span className="profile-info-chip-label">Style</span>
                  {profile.playstyle}
                </span>
              )}
            </div>
          )
        )}

        {/* ── Paddle Setup ───────────────────────────────────── */}
        {editMode ? (
          <div className="profile-edit-section">
            <div className="profile-edit-gear-header">
              <label className="profile-edit-label">Paddle Setup</label>
              <div className="profile-gear-toggle-wrap">
                <span className="profile-gear-toggle-label">Advanced</span>
                <button
                  type="button"
                  className={`profile-gear-toggle ${advancedGear ? 'on' : ''}`}
                  onClick={() => toggleAdvancedGear(!advancedGear)}
                  aria-label="Toggle advanced paddle setup"
                >
                  <span className="profile-gear-toggle-knob" />
                </button>
              </div>
            </div>

            {advancedGear ? (
              <div className="profile-edit-gear">
                <div className="profile-edit-gear-row">
                  <span className="profile-gear-key">Blade</span>
                  <input
                    className="profile-edit-input"
                    placeholder="e.g. Viscaria"
                    value={draftBlade}
                    onChange={e => setDraftBlade(e.target.value)}
                    maxLength={60}
                  />
                </div>
                <div className="profile-edit-gear-row">
                  <span className="profile-gear-key">FH Rubber</span>
                  <input
                    className="profile-edit-input"
                    placeholder="e.g. Tenergy 05"
                    value={draftFhRubber}
                    onChange={e => setDraftFhRubber(e.target.value)}
                    maxLength={60}
                  />
                </div>
                <div className="profile-edit-gear-row">
                  <span className="profile-gear-key">BH Rubber</span>
                  <input
                    className="profile-edit-input"
                    placeholder="e.g. Rozena"
                    value={draftBhRubber}
                    onChange={e => setDraftBhRubber(e.target.value)}
                    maxLength={60}
                  />
                </div>
              </div>
            ) : (
              <div className="profile-edit-gear-row">
                <span className="profile-gear-key">Paddle</span>
                <input
                  className="profile-edit-input"
                  placeholder="e.g. Stiga Raptor"
                  value={draftPaddle}
                  onChange={e => setDraftPaddle(e.target.value)}
                  maxLength={60}
                />
              </div>
            )}
          </div>
        ) : (
          hasGear && (
            <div className="profile-section">
              <h2 className="profile-section-title">Paddle Setup</h2>
              <div className="profile-gear-card">
                {profile.blade && (
                  <div className="profile-gear-row">
                    <span className="profile-gear-key">Blade</span>
                    <span className="profile-gear-val">{profile.blade}</span>
                  </div>
                )}
                {profile.fh_rubber && (
                  <div className="profile-gear-row">
                    <span className="profile-gear-key">FH Rubber</span>
                    <span className="profile-gear-val">{profile.fh_rubber}</span>
                  </div>
                )}
                {profile.bh_rubber && (
                  <div className="profile-gear-row">
                    <span className="profile-gear-key">BH Rubber</span>
                    <span className="profile-gear-val">{profile.bh_rubber}</span>
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {/* ── Location ───────────────────────────────────────── */}
        {editMode && (
          <div className="profile-edit-section">
            <label className="profile-edit-label">Location</label>
            <div className="profile-edit-field">
              <span className="profile-edit-sublabel">Country</span>
              <CustomSelect
                value={draftCountry}
                onChange={v => { setDraftCountry(v); setDraftStateRegion(''); setDraftCity(''); }}
                placeholder="Not set"
                options={[
                  { value: '', label: 'Not set' },
                  ...COUNTRIES.map(c => ({ value: c, label: c })),
                ]}
              />
            </div>

            {draftCountry && (
              <div className="profile-edit-row">
                <div className="profile-edit-field">
                  <span className="profile-edit-sublabel">
                    {draftCountry === 'United States' ? 'State' : 'Region'}
                  </span>
                  {draftCountry === 'United States' ? (
                    <CustomSelect
                      value={draftStateRegion}
                      onChange={v => { setDraftStateRegion(v); setDraftCity(''); }}
                      placeholder="Select state…"
                      options={[
                        { value: '', label: 'Select state…' },
                        ...US_STATES.map(s => ({ value: s.abbr, label: s.name })),
                      ]}
                    />
                  ) : (
                    <input
                      className="profile-edit-input"
                      placeholder="Region / province…"
                      value={draftStateRegion}
                      onChange={e => setDraftStateRegion(e.target.value)}
                      maxLength={60}
                    />
                  )}
                </div>

                <div className="profile-edit-field">
                  <span className="profile-edit-sublabel">City</span>
                  {draftCountry === 'United States' && draftStateRegion && CITIES_BY_STATE[draftStateRegion] ? (
                    <CustomSelect
                      value={draftCity}
                      onChange={v => setDraftCity(v)}
                      placeholder="Select city…"
                      options={[
                        { value: '', label: 'Select city…' },
                        ...CITIES_BY_STATE[draftStateRegion].map(city => ({ value: city, label: city })),
                      ]}
                    />
                  ) : (
                    <input
                      className="profile-edit-input"
                      placeholder="City…"
                      value={draftCity}
                      onChange={e => setDraftCity(e.target.value)}
                      maxLength={60}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Edit mode save / cancel bar ─────────────────────── */}
        {editMode && (
          <div className="profile-edit-actions">
            {saveError && <p className="profile-edit-error">{saveError}</p>}
            <div className="profile-edit-buttons">
              <button className="profile-cancel-btn" onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
              <button className="profile-save-btn" onClick={saveProfile} disabled={saving || uploadingAvatar}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* ── Stats + history — hidden while editing ──────────── */}
        {!editMode && (
          <>
            {/* Singles / Doubles tab switcher — only shown when rated */}
            {profile.is_rated && (
              <div className="dashboard-mode-toggle">
                <button
                  className={`dashboard-mode-btn ${statsMode === 'singles' ? 'dashboard-mode-btn--active' : ''}`}
                  onClick={() => setStatsMode('singles')}
                >
                  Singles
                </button>
                <button
                  className={`dashboard-mode-btn ${statsMode === 'doubles' ? 'dashboard-mode-btn--active' : ''}`}
                  onClick={() => setStatsMode('doubles')}
                >
                  Doubles
                </button>
              </div>
            )}

            {/* ELO hero card */}
            <Card className="profile-elo-card" highlighted>
              <span className="elo-number">
                {!profile.is_rated
                  ? 'Unrated'
                  : statsMode === 'doubles'
                    ? (profile.doubles_elo ?? profile.elo_rating)
                    : profile.elo_rating
                }
              </span>
              <ReliabilityBar
                matchesPlayed={statsMode === 'doubles' ? (profile.doubles_matches_played ?? 0) : (profile.matches_played ?? 0)}
                uniqueOpponents={statsMode === 'doubles' ? (profile.doubles_unique_opponents_count ?? 0) : (profile.unique_opponents_count ?? 0)}
              />
            </Card>

            {/* Stats grid */}
            <div className="stats-grid">
              <Card className="stat-card">
                <span className="stat-value">{statsMode === 'doubles' ? (profile.doubles_wins ?? 0) : profile.wins}</span>
                <span className="stat-label">Wins</span>
              </Card>
              <Card className="stat-card">
                <span className="stat-value">{statsMode === 'doubles' ? (profile.doubles_losses ?? 0) : profile.losses}</span>
                <span className="stat-label">Losses</span>
              </Card>
              <Card className="stat-card">
                <span className="stat-value">{winRate}</span>
                <span className="stat-label">Win Rate</span>
              </Card>
            </div>

            {/* Clubs */}
            {clubs.length > 0 && (
              <div className="profile-section">
                <h2 className="profile-section-title">Clubs</h2>
                <div className="profile-clubs">
                  {clubs.map(club => (
                    <span key={club.id} className="profile-club-chip">
                      {club.is_verified && (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"
                          className="profile-verified-icon">
                          <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                        </svg>
                      )}
                      {club.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ELO history graph */}
            <div className="profile-section">
              <h2 className="profile-section-title">Rating History</h2>
              <EloChart userId={profile.id} isDoubles={statsMode === 'doubles'} />
            </div>

            {/* Match history */}
            <div className="profile-section">
              <h2 className="profile-section-title">Match History</h2>
              <MatchHistory userId={profile.id} limit={5} viewMoreHref={`/match-history/${profile.username}`} />
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default PlayerProfile;
